import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, UseFormReset, UseFormSetValue, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  ContentListDescription,
  ContentListTitle,
  FormInputText,
  Grid,
  InputLabel,
  Select,
  Skeleton,
} from '@hopskipdrive/react-components';
import { LOCATION_TYPES } from '@rideiq/constants/locationTypes';
import { CalendarDropdown } from '@rideiq/shared/components';
import { Text } from '@rideiq/shared/components/Text';
import { colorTokens } from '@rideiq/shared/components/variables';
import { FormA11yErrorAnnouncer } from '@rideiq/shared/features/FormA11yErrorAnnouncer';
import { capitalize } from 'lodash';
import * as Yup from 'yup';

import { LocationFormData } from '../../../types';
import { DuplicationLocation } from '../../DuplicateLocation';
import { usePlaces } from '../LocationForm/usePlaces';
import { LocationMap } from '../LocationMap';
import { SetupPreciseCoords } from '../SetupPreciseCoords';

import { FormContainer } from './LocationForm.styles';

const locationValidationSchema = (locationId) => {
  return Yup.object().shape({
    name: Yup.string().trim().required('Name is Required'),
    pickupProcedureInstructions: Yup.string().test(
      'rider notes validation',
      'Max. 500 characters',
      function riderNotesTest(value) {
        const context = this.options.context as { originalPickupProcedureInstructions: string };
        const originalPickupProcedureInstructions = context.originalPickupProcedureInstructions;
        const wasChanged = value !== originalPickupProcedureInstructions;

        if (!wasChanged) {
          return true; // If the Rider notes weren't changed, skip validation
        }

        return !value || value.length <= 500;
      }
    ),
    dropoffProcedureInstructions: Yup.string().test(
      'rider notes validation',
      'Max. 500 characters',
      function riderNotesTest(value) {
        const context = this.options.context as { originalDropoffProcedureInstructions: string };
        const originalDropoffProcedureInstructions = context.originalDropoffProcedureInstructions;
        const wasChanged = value !== originalDropoffProcedureInstructions;

        if (!wasChanged) {
          return true; // If the Rider notes weren't changed, skip validation
        }

        return !value || value.length <= 500;
      }
    ),
    placeId: Yup.string().required('Required Field'),
    locationType: Yup.string().required('Type is Required'),
    ...(!locationId && { address: Yup.string().required('Address is Required') }),
  });
};

interface LocationFormProps {
  isFetching?: boolean;
  isModal?: boolean;
  initialValues?: AccountLocation;
  calendarSlug?: string;
  onSubmit: (data: LocationFormData) => void;
  onCancel: () => void;
  onSettingPin?: (isSettingPin) => void;
}

export const LocationForm: FC<LocationFormProps> = ({
  isFetching,
  isModal,
  initialValues,
  calendarSlug,
  onSubmit,
  onCancel,
  onSettingPin,
}) => {
  const [isSettingPrecisePin, setIsSettingPrecisePin] = useState(false);

  // Pre-filled values that should be preserved when clearing/changing address (e.g., locationType)
  const prefilledDefaults: LocationFormData = useMemo(() => {
    return {
      name: '',
      address: '',
      streetAddressTwo: '',
      dropoffProcedureInstructions: '',
      pickupProcedureInstructions: '',
      calendarSlug: '',
      placeId: '',
      locationType: initialValues?.locationType || '', // Preserve pre-filled locationType
      preciseLocation: undefined,
    };
  }, [initialValues?.locationType]);

  const defaultState: LocationFormData = useMemo(() => {
    if (initialValues) {
      return {
        name: initialValues.name,
        dropoffProcedureInstructions: initialValues.dropoffProcedure?.instructions || '',
        pickupProcedureInstructions: initialValues.pickupProcedure?.instructions || '',
        calendarSlug: initialValues.calendar?.slug || calendarSlug || '',
        placeId: initialValues.placeId || '',
        locationType: initialValues.locationType || '',
        preciseLocation: initialValues.preciseLocation,
        address: initialValues.address,
        streetAddressTwo: initialValues.streetAddressTwo,
      };
    }

    return prefilledDefaults;
  }, [initialValues, prefilledDefaults]);

  const methods = useForm<LocationFormData>({
    defaultValues: useMemo(() => {
      return defaultState;
    }, [defaultState]),
    context: {
      originalPickupProcedureInstructions: defaultState.pickupProcedureInstructions,
      originalDropoffProcedureInstructions: defaultState.dropoffProcedureInstructions,
    },
    resolver: yupResolver(locationValidationSchema(initialValues?.id)),
  });

  const {
    handleSubmit,
    register,
    reset,
    resetField,
    control,
    setValue,
    formState: { errors, isDirty },
  } = methods;

  const watchAddress = useWatch({ control, name: 'address', defaultValue: '' });
  const watchPreciseLocation = useWatch({ control, name: 'preciseLocation' });

  const mapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    reset(defaultState);
  }, [defaultState]);

  const coordinates =
    initialValues?.lat && initialValues?.lng ? { lat: initialValues?.lat, lng: initialValues?.lng } : undefined;

  const scrollToMap = () => {
    // Make sure map component is rendered
    setTimeout(() => {
      if (mapRef.current && window.innerWidth <= 600) {
        mapRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  const onAdrressChangeResetFields = () => {
    resetField('placeId');
    resetField('name', { defaultValue: '' });
    resetField('locationType');
    resetField('streetAddressTwo', { defaultValue: '' });
    resetField('calendarSlug');
    resetField('preciseLocation');
    resetField('pickupProcedureInstructions');
    resetField('dropoffProcedureInstructions');
  };

  return (
    <FormContainer>
      <FormProvider {...methods}>
        <FormA11yErrorAnnouncer errors={errors} keyMapper={{ placeId: null }} />
        <form
          id="new-location-form"
          aria-label="form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit((data: LocationFormData) =>
              !initialValues?.id || isDirty || watchPreciseLocation !== initialValues?.preciseLocation
                ? onSubmit(data)
                : onCancel()
            )();
          }}
        >
          <input type="hidden" {...register('placeId')} />
          <Grid container direction="column">
            <Grid container direction="row" spacing={24}>
              <Grid item xs={12} md={7} lg={7}>
                <Text mb="10px" fontSize="16px" fontWeight={700} textColor={colorTokens.primary.main}>
                  Address information
                </Text>
                {!isFetching && !initialValues?.id && (
                  <>
                    <ContentEditWrapper id="address" title="Address" fieldType="required">
                      <Controller
                        control={control}
                        name="address"
                        render={({ field: { onChange, value } }) =>
                          isFetching ? (
                            <Skeleton width={200} />
                          ) : (
                            <AddressDropdown
                              locationDetails={initialValues}
                              onChange={onChange}
                              setValue={setValue}
                              reset={onAdrressChangeResetFields}
                              prefilledDefaults={prefilledDefaults}
                              disabled={isSettingPrecisePin}
                              value={value}
                              error={!!errors.address}
                              helperText={errors.address?.message}
                            />
                          )
                        }
                      />
                    </ContentEditWrapper>
                    <ContentEditWrapper id="streetAddressTwo" title="Apt #, Suite, Floor, etc">
                      <Controller
                        control={control}
                        name="streetAddressTwo"
                        render={({ field: { onChange, value } }) =>
                          isFetching ? (
                            <Skeleton width={200} />
                          ) : (
                            <FormInputText
                              id="streetAddressTwo"
                              variant="outlined"
                              name="streetAddressTwo"
                              data-testid="streetAddressTwo"
                              placeholder="Apt/suite number, etc"
                              fullWidth
                              value={value}
                              error={!!errors.streetAddressTwo}
                              helperText={errors.streetAddressTwo?.message}
                              onChange={onChange}
                            />
                          )
                        }
                      />
                    </ContentEditWrapper>
                  </>
                )}
                {!isFetching && initialValues?.id && (
                  <ContentEditWrapper id="address" title="Address" alignTop>
                    <Box width="100%">
                      <Text>{initialValues.address}</Text>
                      <Text textColor={colorTokens.text.medium} mt="8px">
                        You cannot change the address of an existing Location. To use a different address, create a new
                        Location.
                      </Text>
                    </Box>
                  </ContentEditWrapper>
                )}
                <ContentEditWrapper id="precise_location" title="Precise location">
                  <Controller
                    control={control}
                    name="preciseLocation"
                    render={({ field: { value } }) => (
                      <SetupPreciseCoords
                        isFetching={isFetching}
                        coordinates={value}
                        isSettingUp={isSettingPrecisePin || (!watchAddress && !initialValues?.id)}
                        onSetup={() => {
                          setIsSettingPrecisePin(true);
                          onSettingPin && onSettingPin(true);
                          scrollToMap();
                        }}
                        onReset={() => {
                          setValue('preciseLocation', undefined);
                        }}
                      />
                    )}
                  />
                </ContentEditWrapper>
                <ContentEditWrapper id="name" title="Name" fieldType="required">
                  <Controller
                    control={control}
                    name="name"
                    render={({ field: { onChange, value } }) =>
                      isFetching ? (
                        <Skeleton width={150} />
                      ) : (
                        <FormInputText
                          id="name"
                          variant="outlined"
                          name="name"
                          value={value}
                          disabled={isSettingPrecisePin}
                          placeholder="Name of the Location"
                          data-testid="name"
                          fullWidth
                          onChange={onChange}
                          error={!!errors.name}
                          helperText={errors.name?.message}
                        />
                      )
                    }
                  />
                </ContentEditWrapper>
                <ContentEditWrapper id="locationType" title="Type" fieldType="required">
                  <Controller
                    control={control}
                    name="locationType"
                    render={({ field: { onChange, value } }) =>
                      isFetching ? (
                        <Skeleton width={100} />
                      ) : (
                        <Select
                          id="locationType"
                          openOnFocus={false}
                          ListboxProps={{ 'aria-labelledby': 'locationType-label' }}
                          options={LOCATION_TYPES.map((type) => ({
                            value: type.value,
                            label: capitalize(type.label),
                          }))}
                          value={value}
                          setValue={onChange}
                          disabled={(!initialValues?.id && !!initialValues?.locationType) || isSettingPrecisePin}
                          renderInput={(params: AutocompleteRenderInputParams) => (
                            <FormInputText
                              {...params}
                              variant="outlined"
                              data-testid="locationType"
                              fullWidth
                              placeholder="Select"
                              error={!!errors.locationType}
                              helperText={errors.locationType?.message}
                            />
                          )}
                        />
                      )
                    }
                  />
                </ContentEditWrapper>
                <ContentEditWrapper id="calendarSlug" title="Calendar">
                  <Controller
                    control={control}
                    name="calendarSlug"
                    render={({ field: { onChange, value } }) =>
                      isFetching ? (
                        <Skeleton width={150} />
                      ) : (
                        <CalendarDropdown
                          key={value || 'empty'}
                          onChange={onChange}
                          disabled={isSettingPrecisePin}
                          value={value}
                          error={!!errors.calendarSlug}
                          helperText={errors.calendarSlug?.message}
                        />
                      )
                    }
                  />
                </ContentEditWrapper>
                <Text mt="24px" mb="10px" fontSize="16px" fontWeight={700} textColor={colorTokens.primary.main}>
                  Instructions for all riders
                </Text>
                <Text textColor={colorTokens.text.medium} mb=".75rem">
                  Add notes below with information useful for CareDrivers at pickup and drop-off for all riders
                </Text>
                <ContentEditWrapper id="pickupProcedureInstructions" title="During pickup">
                  <Controller
                    control={control}
                    name="pickupProcedureInstructions"
                    render={({ field: { value, onChange } }) =>
                      isFetching ? (
                        <Skeleton width={300} />
                      ) : (
                        <FormInputText
                          id="pickupProcedureInstructions"
                          variant="outlined"
                          name="pickupProcedureInstructions"
                          data-testid="pickupProcedureInstructions"
                          placeholder="Ex: School parking lot, Zone A, spot #3. Look for volunteer with orange vest"
                          fullWidth
                          multiline
                          rows={3}
                          value={value}
                          disabled={isSettingPrecisePin}
                          error={!!errors.pickupProcedureInstructions}
                          helperText={(() => {
                            if (errors.pickupProcedureInstructions) {
                              return errors.pickupProcedureInstructions?.message;
                            }
                            if (value.length === 0) {
                              return <span style={{ color: '#6B7280' }}>Max. 500 characters</span>;
                            }

                            return (
                              <span>
                                {value.length >= 500
                                  ? `Max. ${Math.min(value.length, 500)} characters`
                                  : `${Math.min(value.length, 500)}/500 characters`}
                              </span>
                            );
                          })()}
                          onChange={onChange}
                        />
                      )
                    }
                  />
                </ContentEditWrapper>
                <ContentEditWrapper id="dropoffProcedureInstructions" title="During drop-off">
                  <Controller
                    control={control}
                    name="dropoffProcedureInstructions"
                    render={({ field: { value, onChange } }) =>
                      isFetching ? (
                        <Skeleton width={300} />
                      ) : (
                        <FormInputText
                          id="dropoffProcedureInstructions"
                          variant="outlined"
                          name="dropoffProcedureInstructions"
                          data-testid="dropoffProcedureInstructions"
                          placeholder="Ex: Bus lane drop-off, look for volunteer with orange vest."
                          multiline
                          rows={3}
                          value={value}
                          fullWidth
                          disabled={isSettingPrecisePin}
                          error={!!errors.dropoffProcedureInstructions}
                          helperText={(() => {
                            if (errors.dropoffProcedureInstructions) {
                              return errors.dropoffProcedureInstructions?.message;
                            }
                            if (value.length === 0) {
                              return <span style={{ color: '#6B7280' }}>Max. 500 characters</span>;
                            }

                            return (
                              <span>
                                {value.length >= 500
                                  ? `Max. ${Math.min(value.length, 500)} characters`
                                  : `${Math.min(value.length, 500)}/500 characters`}
                              </span>
                            );
                          })()}
                          onChange={onChange}
                        />
                      )
                    }
                  />
                </ContentEditWrapper>
              </Grid>
              <Grid ref={mapRef} item xs={12} md={5} lg={5} paddingLeft={`${isModal ? '24px' : '72px'} !important`}>
                <LocationMap
                  control={control}
                  isModal={isModal}
                  canDragMarker={isSettingPrecisePin}
                  onCancel={() => {
                    setIsSettingPrecisePin(false);
                    onSettingPin && onSettingPin(false);
                  }}
                  onApply={(preciseCoordinates) => {
                    setValue('preciseLocation', preciseCoordinates);
                    setIsSettingPrecisePin(false);
                    onSettingPin && onSettingPin(false);
                  }}
                  onSetup={() => {
                    setIsSettingPrecisePin(true);
                    onSettingPin && onSettingPin(true);
                    scrollToMap();
                  }}
                  coordinates={coordinates}
                  isExistingLocation={!!initialValues?.id}
                />
              </Grid>
            </Grid>
          </Grid>
          <DuplicationLocation />
        </form>
      </FormProvider>
    </FormContainer>
  );
};

interface AddressDropdownProps {
  locationDetails?: AccountLocation;
  disabled: boolean;
  onChange: (e) => void;
  setValue: UseFormSetValue<LocationFormData>;
  reset: UseFormReset<LocationFormData>;
  prefilledDefaults: LocationFormData;
  value?: string;
  error: boolean;
  helperText?: string;
}

const AddressDropdown: FC<AddressDropdownProps> = ({
  locationDetails,
  disabled,
  onChange,
  setValue,
  reset,
  value,
  error,
  helperText,
}) => {
  const selectedPlace = locationDetails && {
    description: locationDetails?.address,
    placeId: locationDetails?.placeId,
    lat: locationDetails?.lat,
    lng: locationDetails?.lng,
  };
  const { debounceHandleQuery, places, isFetching } = usePlaces(selectedPlace);

  return (
    <Autocomplete
      id="address"
      freeSolo
      isLoading={isFetching}
      filterOptions={() => places}
      options={places}
      value={value}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => typeof value === 'string' && option.description === value}
      getOptionLabel={(location) =>
        typeof location === 'string' ? location : location.description.replace(', USA', '')
      }
      onInputChange={(_, newInputValue) => {
        // Clear form when user manually clears the input field
        if (!newInputValue && value) {
          debounceHandleQuery('');
          reset();
        }
      }}
      onChange={(_, location) => {
        if (typeof location === 'string') {
          return;
        }

        // Clear address but preserve pre-filled values like locationType
        debounceHandleQuery('');
        reset();
        if (!location) {
          onChange('');
        }

        const existingPlaceName = location?.structuredFormatting?.mainText || '';
        const hasNumericDigits = /\d/.test(existingPlaceName);

        setValue('name', hasNumericDigits ? '' : existingPlaceName);
        setValue('placeId', location?.placeId, { shouldValidate: true });
        onChange(location?.description.replace(', USA', ''));
      }}
      fullWidth
      renderInput={(params) => (
        <FormInputText
          {...params}
          variant="outlined"
          fullWidth
          onChange={(e) => {
            debounceHandleQuery(e.target.value);
            onChange(e);
          }}
          value={value}
          placeholder="Search"
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

export interface ContentEditWrapperProps {
  id: string;
  title: string;
  fieldType?: string;
  children: string | JSX.Element;
  alignTop?: boolean;
}

const ContentEditWrapper = ({ id, title, fieldType, children, alignTop = false }: ContentEditWrapperProps) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems={alignTop ? 'flex-start' : undefined}
      id={id + '_edit_field'}
      mb=".75rem"
    >
      <ContentListTitle style={alignTop ? { flex: '0 0 30%', minHeight: 0, alignItems: 'flex-start' } : undefined}>
        <InputLabel id={`${id}-label`} htmlFor={id} fieldtype={fieldType}>
          {title}
        </InputLabel>
      </ContentListTitle>
      <ContentListDescription
        style={
          alignTop
            ? { flex: '0 0 70%', minWidth: 0, minHeight: 0, marginLeft: 0, alignItems: 'flex-start' }
            : { minWidth: '70%' }
        }
      >
        {children}
      </ContentListDescription>
    </Box>
  );
};
